// R-167 WU-6a — Admin /admin/counters API client.
//
// Backed by the WU-5 routes at /api/custom-counters (5 endpoints):
//   GET    /api/custom-counters         → { counters: CustomCounter[] }
//   POST   /api/custom-counters         → { counter: CustomCounter }
//   PATCH  /api/custom-counters/:id     → { counter: CustomCounter }
//   POST   /api/custom-counters/:id/reset → { counter: CustomCounter }
//   DELETE /api/custom-counters/:id     → 204 OR 409 { code: "COUNTER_IN_USE", references }
//
// NOTE: paths here are written WITHOUT the leading "/api" because the shared
// `api` client in `@/lib/api-client` already prefixes it (see api-client.ts).
// Errors surface as `ApiError(status, body)` — the page layer inspects
// `.status === 409` + `.body.code === "COUNTER_IN_USE"` for the delete-guard.
import { api } from "@/lib/api-client";

export type ResetCadence = "never" | "daily" | "weekly" | "monthly" | "yearly";

export interface CustomCounter {
  id: string;
  tenantId: string;
  jobTypeId: string | null;
  name: string;
  minValue: number;
  maxValue: number;
  paddingWidth: number;
  resetCadence: ResetCadence;
  currentValue: number;
  currentPeriodKey: string | null;
  exhaustedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CounterReference {
  scope: "tenant" | "job-type";
  scopeId: string;
  scopeName: string;
  slot: "batch" | "document" | "page";
  template: string;
}

export interface CreateCounterPayload {
  name: string;
  jobTypeId?: string | null;
  minValue: number;
  maxValue: number;
  paddingWidth?: number;
  resetCadence: ResetCadence;
}

export interface UpdateCounterPayload {
  minValue?: number;
  maxValue?: number;
  paddingWidth?: number;
  resetCadence?: ResetCadence;
}

export const countersApi = {
  list: () => api.get<{ counters: CustomCounter[] }>("/custom-counters"),
  create: (payload: CreateCounterPayload) =>
    api.post<{ counter: CustomCounter }>("/custom-counters", payload),
  update: (id: string, payload: UpdateCounterPayload) =>
    api.patch<{ counter: CustomCounter }>(`/custom-counters/${id}`, payload),
  reset: (id: string) =>
    api.post<{ counter: CustomCounter }>(`/custom-counters/${id}/reset`, {}),
  delete: (id: string) => api.delete<void>(`/custom-counters/${id}`),
};
